import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { Button } from '@carbon/react';
import { SCENARIO_DOMAINS } from 'lib/evaluation-case/scenario-domains';
import { MapBoundary } from '@carbon/react/icons';
import {
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import { Link } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import {
  createEvaluationCaseSearchAtom,
  type EvaluationCaseParams,
} from './atoms';
import type { EvaluationCaseResult } from './types';
import { EvaluationCasesFilters } from './evaluation-cases-filters';
import {
  EvaluationCaseSummaryCards,
  type SummaryCardType,
} from './summary-cards';
import { EvaluationCasesCoverageMapStrip } from './evaluation-cases-coverage-map-strip';
import { DEFAULT_PARAMS, SORT_FIELD_MAP, columnFiltersToParams } from './utils';
import styles from './evaluation-cases-content.module.scss';

type EcRow = EvaluationCaseResult & Record<string, unknown>;

const columns: DataTableColumnDef<EcRow>[] = [
  {
    accessorKey: 'name',
    header: 'Case',
    size: 300,
    cell: ({ row }) => <Link to={row.original.href}>{row.original.name}</Link>,
  },
  {
    accessorKey: 'evaluationType',
    header: 'Evaluation Type',
    size: 160,
    meta: { type: 'enum' },
    enableSorting: true,
  },
  {
    accessorKey: 'createdBy',
    header: 'Created By',
    size: 160,
    meta: { type: 'enum' },
    enableSorting: false,
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    size: 180,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'updatedBy',
    header: 'Updated By',
    size: 160,
    enableSorting: false,
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated At',
    size: 180,
    meta: { type: 'date' },
  },
];

// How many of the domains the case assigns, and whether it could run.
const completenessColumn: DataTableColumnDef<EcRow> = {
  accessorKey: 'complete',
  header: 'Completeness',
  size: 180,
  enableSorting: false,
  cell: ({ row }) =>
    `${row.original.assignedDomainCount ?? 0} of ${SCENARIO_DOMAINS.length} · ${
      row.original.complete ? 'Complete' : 'Incomplete'
    }`,
};

const tableColumns: DataTableColumnDef<EcRow>[] = [
  columns[0],
  completenessColumn,
  ...columns.slice(1),
];

const initialTableState: InitialTableState = {
  columnPinning: { left: ['name'] },
  sortColumn: 'createdAt',
  sortDirection: 'desc',
};

const EvaluationCasesContent: FC = () => {
  const { searchAtom } = useMemo(() => createEvaluationCaseSearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const [params, setParams] = useState<EvaluationCaseParams>(DEFAULT_PARAMS);
  const [isMapOpen, setIsMapOpen] = useState(true);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);

  const results = searchResult.data?.results;

  // The map fits to the selected row's coverageBbox, which the list row carries.
  const selectedBounds = useMemo<
    [[number, number], [number, number]] | null
  >(() => {
    const box = results?.find((row) => row.id === selectedCaseId)?.coverageBbox;
    return box && box.every(Number.isFinite)
      ? [
          [box[0], box[1]],
          [box[2], box[3]],
        ]
      : null;
  }, [results, selectedCaseId]);

  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  const updateParams = useCallback((updates: Partial<EvaluationCaseParams>) => {
    setParams((current) => ({ ...current, page: 1, ...updates }));
  }, []);

  const handleColumnFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      updateParams(columnFiltersToParams(filters));
    },
    [updateParams]
  );

  const handleSortChange = useCallback(
    (sorting: SortingState) => {
      if (sorting.length === 0) {
        updateParams({ sortAttribute: 'created_at', sortDirection: 'DESC' });
      } else {
        const sort = sorting[0];
        updateParams({
          sortAttribute: SORT_FIELD_MAP[sort.id] ?? sort.id,
          sortDirection: sort.desc ? 'DESC' : 'ASC',
        });
      }
    },
    [updateParams]
  );

  const handleGlobalSearchChange = useCallback(
    (search: string) => {
      const newText = search || undefined;
      if (params.text === newText) return;
      updateParams({ text: newText });
    },
    [params.text, updateParams]
  );

  const handleSummaryCardClick = useCallback(
    (card: SummaryCardType) => {
      const now = moment();
      const daysBack = { last24h: 1, last7d: 7, lastMonth: 30 };
      const start = now.clone().subtract(daysBack[card], 'days');
      updateParams({
        createdAt: {
          start: start.format('YYYY-MM-DD'),
          end: now.format('YYYY-MM-DD'),
        },
      });
    },
    [updateParams]
  );

  const handleRowClick = useCallback((rowId: string | null) => {
    setSelectedCaseId(rowId ? Number(rowId) : null);
  }, []);

  const handleMapClick = useCallback((evaluationCaseId: number) => {
    window.open(`/evaluation_cases/${evaluationCaseId}`, '_blank');
  }, []);

  const totalCount = searchResult.data?.totalCount ?? 0;
  const coverageVersion =
    (searchResult.data as { coverageVersion?: string | null } | undefined)
      ?.coverageVersion ?? null;

  const handleLoadData = useCallback(({ page, pageSize }: TableState) => {
    setParams((current) => ({ ...current, page, pageSize }));
  }, []);

  return (
    <div className={styles.contentWrapper}>
      <EvaluationCaseSummaryCards
        aggregations={searchResult.data?.aggregations}
        onCardClick={handleSummaryCardClick}
      />
      <div className={styles.tableWrapper}>
        <DataTable<EcRow>
          data={(results ?? []) as EcRow[]}
          columns={tableColumns}
          getRowId={(row) => String(row.id)}
          initialTableState={initialTableState}
          persistenceKey="evaluation-cases-list-table"
          config={{
            columnFilters: false,
            columnReordering: false,
            selectionType: 'single',
          }}
          isLoading={searchResult.loading}
          error={searchResult.error ?? null}
          scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
          onPaginationChange={handleLoadData}
          onSortChange={(s) => handleSortChange(s.sorting)}
          onGlobalSearchChange={(s) => handleGlobalSearchChange(s.globalFilter)}
          onFilterChange={(s) => handleColumnFilterChange(s.columnFilters)}
          onSelectionChange={(s) =>
            handleRowClick(s.selection.toggledNodes[0] ?? null)
          }
          searchPlaceholder="Search by name..."
          renderFilterPanel={() => (
            <EvaluationCasesFilters facets={searchResult.data?.facets} />
          )}
          leftToolbarActions={[
            <Button
              key="map-toggle"
              kind="ghost"
              size="md"
              hasIconOnly
              renderIcon={MapBoundary}
              iconDescription="Toggle map"
              onClick={() => setIsMapOpen(!isMapOpen)}
            />,
          ]}
          betweenToolbarAndContent={
            isMapOpen ? (
              <EvaluationCasesCoverageMapStrip
                params={params}
                coverageVersion={coverageVersion}
                selectedCaseId={selectedCaseId}
                selectedBounds={selectedBounds}
                onMapClick={handleMapClick}
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
};

export default EvaluationCasesContent;
